import { useAppearance } from '../../appearance/AppearanceProvider';
import type { AppearanceAccent, AppearanceDensity, AppearanceKey, AppearanceMotion, AppearanceTheme } from '../../appearance/types';

const themes = [
  { value: 'dark', label: 'Тёмная' },
  { value: 'light', label: 'Светлая' },
  { value: 'system', label: 'Системная' },
] as const;

const accents = [
  { value: 'blue', label: 'Синий' },
  { value: 'purple', label: 'Фиолетовый' },
  { value: 'green', label: 'Зелёный' },
  { value: 'orange', label: 'Оранжевый' },
  { value: 'pink', label: 'Розовый' },
] as const;

const densityOptions: ReadonlyArray<{ value: AppearanceDensity; label: string }> = [
  { value: 'comfortable', label: 'Комфортная' },
  { value: 'compact', label: 'Компактная' },
];

const motionOptions: ReadonlyArray<{ value: AppearanceMotion; label: string }> = [
  { value: 'full', label: 'Полная' },
  { value: 'reduced', label: 'Уменьшенная' },
];

const swatchColors: Record<AppearanceAccent, string> = {
  blue: '#2563eb',
  purple: '#7c3aed',
  green: '#15803d',
  orange: '#c2410c',
  pink: '#be185d',
};

const fieldLabels: Record<AppearanceKey, string> = {
  theme: 'Тема оформления',
  accent: 'Акцент',
  font_size: 'Размер шрифта',
  density: 'Плотность интерфейса',
  motion: 'Анимация',
};

const AppearanceSettings = () => {
  const { settings, loading, loadError, fieldErrors, saving, saved, updateAppearance, retryAppearance } = useAppearance();
  const themeLabel = themes.find((theme) => theme.value === settings.theme)?.label;
  const densityLabel = densityOptions.find((density) => density.value === settings.density)?.label;

  if (loading) {
    return <p role="status" className="shark-muted py-4 text-sm">Загрузка настроек оформления...</p>;
  }

  if (loadError) {
    return (
      <section aria-label="Настройки оформления" className="shark-card shark-border rounded-2xl border">
        <p role="alert" className="shark-error text-sm">{loadError}</p>
        <button type="button" onClick={retryAppearance} className="shark-accent-button mt-4 rounded-xl px-4 py-2 text-sm font-bold">
          Повторить
        </button>
      </section>
    );
  }

  const savingMessage = (field: AppearanceKey) => saving[field] && (
    <p role="status" aria-label={`${fieldLabels[field]}: сохранение`} className="shark-muted mt-2 text-sm">Сохранение...</p>
  );
  const savedMessage = (field: AppearanceKey) => saved[field] && (
    <p role="status" aria-label={`${fieldLabels[field]}: сохранено`} className="shark-muted mt-2 text-sm">Сохранено</p>
  );
  const fieldError = (field: AppearanceKey) => fieldErrors[field] && (
    <p role="alert" className="shark-error mt-2 text-sm">{fieldErrors[field]}</p>
  );

  return (
    <section aria-label="Настройки оформления" className="shark-density-stack">
      <article className="shark-card shark-border rounded-2xl border">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="shark-accent-button flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-black">S</span>
          <div className="min-w-0">
            <h4 className="text-lg font-bold text-white">Предпросмотр Sharkly</h4>
            <p className="shark-muted text-sm">Так будет выглядеть интерфейс с выбранными настройками.</p>
            <p className="shark-muted mt-1 text-xs">{themeLabel} · {settings.font_size} px · {densityLabel}</p>
          </div>
        </div>
        <button type="button" className="shark-accent-button shark-density-control mt-4 rounded-xl text-sm font-bold">Пример действия</button>
      </article>

      <fieldset className="shark-card shark-border rounded-2xl border">
        <legend className="px-1 text-lg font-bold text-white">Тема оформления</legend>
        <div className="shark-density-list mt-3 grid grid-cols-1 sm:grid-cols-3">
          {themes.map((theme) => (
            <button
              key={theme.value}
              type="button"
              aria-pressed={settings.theme === theme.value}
              onClick={() => updateAppearance('theme', theme.value as AppearanceTheme)}
              className={`shark-density-control rounded-xl border text-sm font-bold transition ${settings.theme === theme.value ? 'border-blue-500 bg-blue-500/10' : 'shark-border hover:bg-gray-800'}`}
            >
              {theme.label}
            </button>
          ))}
        </div>
        {savingMessage('theme')}
        {savedMessage('theme')}
        {fieldError('theme')}
      </fieldset>

      <fieldset className="shark-card shark-border rounded-2xl border">
        <legend className="px-1 text-lg font-bold text-white">Акцент</legend>
        <div className="shark-density-list mt-3 grid grid-cols-5">
          {accents.map((accent) => (
            <button
              key={accent.value}
              type="button"
              aria-label={accent.label}
              aria-pressed={settings.accent === accent.value}
              onClick={() => updateAppearance('accent', accent.value as AppearanceAccent)}
              className={`shark-density-control flex min-w-0 flex-col items-center gap-2 rounded-xl border text-xs font-bold ${settings.accent === accent.value ? 'border-blue-500 bg-blue-500/10' : 'shark-border hover:bg-gray-800'}`}
            >
              <span aria-hidden="true" className="h-7 w-7 rounded-full" style={{ backgroundColor: swatchColors[accent.value] }} />
              <span className="truncate">{accent.label}</span>
            </button>
          ))}
        </div>
        {savingMessage('accent')}
        {savedMessage('accent')}
        {fieldError('accent')}
      </fieldset>

      <fieldset className="shark-card shark-border rounded-2xl border">
        <legend className="px-1 text-lg font-bold text-white">Размер шрифта</legend>
        <div className="mt-3 flex items-center gap-4">
          <input
            id="appearance-font-size"
            aria-label="Размер шрифта"
            type="range"
            min="13"
            max="18"
            value={settings.font_size}
            onChange={(event) => updateAppearance('font_size', Number(event.target.value))}
            className="w-full accent-blue-500"
          />
          <output className="shark-muted w-12 shrink-0 text-right text-sm">{settings.font_size} px</output>
        </div>
        {savingMessage('font_size')}
        {savedMessage('font_size')}
        {fieldError('font_size')}
      </fieldset>

      <fieldset className="shark-card shark-border rounded-2xl border">
        <legend className="px-1 text-lg font-bold text-white">Плотность интерфейса</legend>
        <div className="shark-density-list mt-3 grid grid-cols-1 sm:grid-cols-2">
          {densityOptions.map((option) => (
            <label key={option.value} className="shark-density-control flex cursor-pointer items-center gap-3 rounded-xl border border-gray-700 text-sm font-bold">
              <input
                type="radio"
                name="appearance-density"
                value={option.value}
                checked={settings.density === option.value}
                aria-checked={settings.density === option.value}
                onChange={() => updateAppearance('density', option.value)}
                className="accent-blue-600"
              />
              {option.label}
            </label>
          ))}
        </div>
        {savingMessage('density')}
        {savedMessage('density')}
        {fieldError('density')}
      </fieldset>

      <fieldset className="shark-card shark-border rounded-2xl border">
        <legend className="px-1 text-lg font-bold text-white">Анимация</legend>
        <div className="shark-density-list mt-3 grid grid-cols-1 sm:grid-cols-2">
          {motionOptions.map((option) => (
            <label key={option.value} className="shark-density-control flex cursor-pointer items-center gap-3 rounded-xl border border-gray-700 text-sm font-bold">
              <input
                type="radio"
                name="appearance-motion"
                value={option.value}
                checked={settings.motion === option.value}
                aria-checked={settings.motion === option.value}
                onChange={() => updateAppearance('motion', option.value)}
                className="accent-blue-600"
              />
              {option.label}
            </label>
          ))}
        </div>
        {savingMessage('motion')}
        {savedMessage('motion')}
        {fieldError('motion')}
      </fieldset>
    </section>
  );
};

export default AppearanceSettings;
